/**
 * Skill- und Talent-Menue (Masterprompt Abschnitt 46,
 * VISUAL_REFERENCE Abschnitte 46a und 46c).
 *
 * Aufbau seit v0.34 - derselbe Rahmen wie Send Wave und Defense:
 *
 *   Kopf     Titel, eine Zeile Erklaerung, SKILLPUNKTE n, grosses X
 *   Links    die Levelstrecke: Lv 1 Startskills, Punkte auf 2, 4 und 6,
 *            Lv 5 das Ultimate ohne Zutun, Lv 8 Talent, Lv 10 Capstone
 *   Rechts   die angetippte Karte im Detail und der eine goldene Knopf
 *
 * Antippen waehlt aus, gebucht wird nur rechts - Produktentscheidung vom
 * 27.09.2026. Bei Talent und Capstone ist das kein Luxus: Die Wahl ist
 * endgueltig, und ein danebengetippter Capstone kostete frueher das Match.
 *
 * Was auf welcher Stufe steht, rechnet skillTreeView.ts aus. Hier steht nur,
 * wie es aussieht.
 *
 * Alles hier ist Vorschau. Ob ein Punkt ankommt, entscheidet der Server.
 */

import { MAX_SKILL_UPGRADE_STEP, getSkillsForHero } from '@dueline/game-data';
import { createContext, useContext, useState } from 'react';
import { t } from '../i18n/index.js';
import type { MatchSnapshot } from '../net/MatchConnection.js';
import {
  buildLevelPlan,
  getChoiceBlocker,
  type LevelRow,
  type SkillChoice,
  type SkillTreeInput,
} from './skillTreeView.js';
import { useTap } from './usePress.js';
import { faehigkeitBild } from './faehigkeitBilder.js';

/** Welcher Held gerade gezeigt wird - fuer die Symbole der Faehigkeiten. */
const HeldKontext = createContext('');

interface Props {
  snapshot: MatchSnapshot;
  onSpend: (slot: number) => void;
  onChooseTalent: (choiceId: string) => void;
  onChooseCapstone: (choiceId: string) => void;
  onClose: () => void;
}

/** Zeichen einer Karte: die Slotnummer, ein Blitz fuers Ultimate, sonst ein Stern. */
function ChoiceMark({ choice }: { choice: SkillChoice }) {
  /*
   * Gibt es ein Symbol fuer die Faehigkeit dieser Karte, steht es statt der
   * Ziffer (16.10.2026). Talente und Capstones haben keines.
   */
  const heroId = useContext(HeldKontext);
  const slot = choice.kind === 'ultimate' ? 4 : choice.slot;
  const skill = slot ? getSkillsForHero(heroId).find((s) => s.slot === slot) : undefined;
  const bild = skill ? faehigkeitBild(skill.id) : undefined;
  if (bild) {
    return (
      <span
        className={`dl-choice__mark dl-choice__mark--${choice.kind} dl-choice__mark--bild`}
        aria-hidden="true"
      >
        <picture className="dl-choice__bild" aria-hidden="true">
          <source type="image/avif" srcSet={bild.avif} sizes="48px" />
          <source type="image/webp" srcSet={bild.webp} sizes="48px" />
          <img src={bild.ersatz} alt="" decoding="async" />
        </picture>
      </span>
    );
  }

  const zeichen =
    choice.kind === 'ultimate'
      ? 'R'
      : choice.slot
        ? String(choice.slot)
        : choice.kind === 'capstone'
          ? '★'
          : '◆';

  return (
    <span className={`dl-choice__mark dl-choice__mark--${choice.kind}`} aria-hidden="true">
      {zeichen}
    </span>
  );
}

/** Eine Karte auf der Strecke. */
function ChoiceCard({
  choice,
  gewaehlt,
  onSelect,
}: {
  choice: SkillChoice;
  gewaehlt: boolean;
  onSelect: () => void;
}) {
  const press = useTap(onSelect);

  return (
    <button
      type="button"
      className={
        `dl-choice dl-choice--${choice.kind} dl-choice--${choice.state}` +
        (gewaehlt ? ' dl-choice--active' : '')
      }
      aria-pressed={gewaehlt}
      {...press}
    >
      <ChoiceMark choice={choice} />
      <span className="dl-choice__name">{choice.name}</span>
      {choice.step && (
        <span className="dl-choice__step">
          {choice.step} / {MAX_SKILL_UPGRADE_STEP}
        </span>
      )}
      {choice.state === 'owned' && (
        <span className="dl-choice__owned" aria-hidden="true">
          ✓
        </span>
      )}
    </button>
  );
}

/** Eine Stufe der Levelstrecke. */
function LevelStep({
  row,
  gewaehlteId,
  onSelect,
}: {
  row: LevelRow;
  gewaehlteId: string;
  onSelect: (choice: SkillChoice) => void;
}) {
  const beschriftung = (t.skillTree.rows as Record<string, string>)[row.kind] ?? '';

  return (
    <div
      className={
        'dl-level' + (row.reached ? ' dl-level--reached' : '') + (row.open ? ' dl-level--open' : '')
      }
    >
      <div className="dl-level__spine" aria-hidden="true">
        <span className="dl-level__diamond" />
      </div>

      <div className="dl-level__body">
        <div className="dl-level__head">
          <span className="dl-level__number">
            {t.skillTree.levelShort} {row.level}
          </span>
          <span className="dl-level__title">{beschriftung}</span>
          {row.kind === 'ultimate' && (
            <span className="dl-level__chip">{t.skillTree.automatic}</span>
          )}
          {!row.reached && (
            <span className="dl-level__chip dl-level__chip--locked">
              {t.skillTree.fromLevel.replace('{n}', String(row.level))}
            </span>
          )}

          {/*
            Der Hinweis zu einem Punkt steht IN der Kopfzeile, nicht darunter:
            Sieben Stufen mal eine eigene Zeile passten auf dem Handy nicht
            mehr ins Fenster.
          */}
          {row.reached && row.kind === 'point' && row.choices.length === 0 && (
            <span className="dl-level__note">
              {row.open ? t.skillTree.pointFree : t.skillTree.pointSpent}
            </span>
          )}
        </div>

        {/*
          Jede Stufe ist genau eine Kartenzeile hoch - auch die, die gerade
          keine Karten hat (Rueckmeldung vom 27.09.2026).

          Sonst wuchs das Fenster in dem Moment, in dem ein Skillpunkt frei
          wurde und die Auswahl erschien. Statt fester Zahlen steht dort eine
          unsichtbare Karte: Die ist auf jedem Geraet automatisch genau so
          hoch wie eine echte.
        */}
        <div className="dl-level__choices">
          {row.choices.length > 0 ? (
            row.choices.map((choice) => (
              <ChoiceCard
                key={choice.id}
                choice={choice}
                gewaehlt={choice.id === gewaehlteId}
                onSelect={() => onSelect(choice)}
              />
            ))
          ) : (
            <span className="dl-choice dl-choice--platzhalter" aria-hidden="true">
              <span className="dl-choice__mark" />
              <span className="dl-choice__name" />
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

export function SkillTreePanel({
  snapshot,
  onSpend,
  onChooseTalent,
  onChooseCapstone,
  onClose,
}: Props) {
  const eingabe: SkillTreeInput = {
    heroId: snapshot.heroId,
    level: snapshot.level,
    skillPoints: snapshot.skillPoints,
    skillUpgrades: snapshot.skillUpgrades,
    talentPending: snapshot.talentPending,
    capstonePending: snapshot.capstonePending,
    talentId: snapshot.talentId,
    capstoneId: snapshot.capstoneId,
  };

  const plan = buildLevelPlan(eingabe);

  /*
   * Ausgewaehlt ist zu Beginn nichts. Sobald ein Punkt oder eine Wahl offen
   * ist, waehlt das Menue die erste offene Karte vor - dann steht rechts
   * gleich etwas Sinnvolles, ohne dass jemand etwas bucht.
   */
  const ersteOffene = plan
    .flatMap((zeile) => zeile.choices)
    .find((choice) => choice.state === 'available');
  const [gewaehlteId, setGewaehlteId] = useState<string>('');

  const alleKarten = plan.flatMap((zeile) => zeile.choices);
  const gewaehlt = alleKarten.find((choice) => choice.id === gewaehlteId) ?? ersteOffene ?? null;

  const blocker = gewaehlt ? getChoiceBlocker(eingabe, gewaehlt) : null;
  const gesperrt = !gewaehlt || blocker !== null;
  const endgueltig = gewaehlt?.kind === 'talent' || gewaehlt?.kind === 'capstone';

  const schliessen = useTap(onClose);
  const buchen = useTap(() => {
    if (!gewaehlt || blocker !== null) return;
    if (gewaehlt.kind === 'point' && gewaehlt.slot) onSpend(gewaehlt.slot);
    if (gewaehlt.kind === 'talent') onChooseTalent(gewaehlt.id);
    if (gewaehlt.kind === 'capstone') onChooseCapstone(gewaehlt.id);
  });

  const knopfText =
    gewaehlt && gewaehlt.kind !== 'start' && gewaehlt.kind !== 'ultimate'
      ? (t.skillTree.confirm as Record<string, string>)[gewaehlt.kind]
      : (t.skillTree.confirm as Record<string, string>).point;

  /* Der Skill, zu dem eine Verbesserung gehoert - fuer die Detailspalte. */
  const grundskill =
    gewaehlt?.kind === 'point' && gewaehlt.slot
      ? getSkillsForHero(snapshot.heroId).find((skill) => skill.slot === gewaehlt.slot)
      : undefined;

  return (
    <HeldKontext.Provider value={snapshot.heroId}>
      <div className="dl-composer dl-skilltree" role="dialog" aria-label={t.skillTree.title}>
        <div className="dl-composer__head dl-composer__head--limit">
          <span className="dl-composer__title">
            {t.skillTree.title} · {t.hero.level} {snapshot.level}
          </span>
          <span
            className={`dl-composer__limit${snapshot.skillPoints > 0 ? ' dl-composer__limit--over' : ''}`}
          >
            <span className="dl-composer__limit-label">{t.skillTree.pointsLabel}</span>
            <span className="dl-composer__limit-value">{snapshot.skillPoints}</span>
          </span>
          <button
            type="button"
            className="dl-composer__close"
            aria-label={t.skillTree.close}
            {...schliessen}
          >
            ✕
          </button>
        </div>

        <div className="dl-composer__main dl-skilltree__plan">
          {plan.map((row) => (
            <LevelStep
              key={row.level}
              row={row}
              gewaehlteId={gewaehlt?.id ?? ''}
              onSelect={(choice) => setGewaehlteId(choice.id)}
            />
          ))}
          <span className="dl-composer__hint">{t.skillTree.afterTen}</span>
        </div>

        <div className="dl-composer__side">
          <span className="dl-summary__title">{t.skillTree.selected}</span>

          {gewaehlt ? (
            <>
              <div className="dl-detail__head">
                <ChoiceMark choice={gewaehlt} />
                <span className="dl-detail__name">{gewaehlt.name}</span>
              </div>

              <div className="dl-detail__chips">
                <span className="dl-creep-card__tag">
                  {(t.skillTree.types as Record<string, string>)[gewaehlt.kind]}
                </span>
                {grundskill && <span className="dl-creep-card__tag">{grundskill.name}</span>}
                {gewaehlt.step && (
                  <span className="dl-creep-card__tag dl-creep-card__tag--points">
                    {t.skillTree.levelShort} {gewaehlt.step} / {MAX_SKILL_UPGRADE_STEP}
                  </span>
                )}
              </div>

              <span className="dl-detail__text">{gewaehlt.description}</span>

              <div className="dl-detail__foot">
                {/*
                Der Hinweis steht immer im Aufbau, auch wenn er nichts zu
                sagen hat (Rueckmeldung vom 27.09.2026): Erschiene er erst bei
                einer endgueltigen Wahl, wuerde das Fenster genau dann hoeher.
              */}
                <span
                  className={
                    'dl-detail__warning' +
                    (endgueltig && blocker === null ? '' : ' dl-detail__warning--leer')
                  }
                  aria-hidden={endgueltig && blocker === null ? undefined : true}
                >
                  {t.skillTree.finalWarning}
                </span>

                <button
                  type="button"
                  className="dl-composer__send dl-detail__cta"
                  disabled={gesperrt}
                  {...buchen}
                >
                  <span className="dl-composer__send-label">{knopfText}</span>
                  {blocker && (
                    <span className="dl-composer__send-reason">
                      {(t.skillTree.blocked as Record<string, string>)[blocker]}
                    </span>
                  )}
                </button>
              </div>
            </>
          ) : (
            <span className="dl-detail__text">{t.skillTree.nothingSelected}</span>
          )}
        </div>
      </div>
    </HeldKontext.Provider>
  );
}
